"use client";

import { useLanguage } from '@/context/LanguageContext'
//import Image from 'next/image'
import { Border } from '@/components/Border'
import { Button } from '@/components/common/Button'
//import { Container } from '@/components/common/Container'
import { FadeIn } from '@/components/FadeIn'
import { Offices } from '@/components/Offices'
import { PageIntro } from '@/components/PageIntro'
import Link from "next/link";
import { useEffect, useId, useRef, useState } from 'react'
import Script from 'next/script'

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: any) => string
      reset: (widgetId: string) => void
    }
  }
}

const texts = {
  en: {
    title: 'Let’s work together',
    contactUs: 'Contact me',
    text: 'I can’t wait to hear from you.',
    emailUs: 'Email me',
    enterpriseDetails: 'Enterprise details',
    workInquiries: 'Work inquiries',
    name: 'Name',
    company: 'Company',
    phone: 'Phone',
    message: 'Message',
    budget: 'Budget',
    moreThan: 'More than',
    currency: '$',
    sending: 'Sending...',
    sent: 'Message sent. Thank you.',
    networkError: 'Network error. Please try again later.',
    submitFailed: 'Submit failed. Please try again.',
    nameRequired: 'Please enter your name.',
    emailRequired: 'Please enter your email address.',
    emailInvalid: "Invalid email address.",
    verifySpam: 'Please verify you are not a spam bot.',
    emailDisposable: "This email address is disposable. Please use a real one.",
  },
  it: {
    title: 'Lavoriamo insieme',
    contactUs: 'Contattami',
    text: "Non vedo l'ora di sentirti.",
    emailUs: 'Scrivimi',
    enterpriseDetails: 'Dettagli azienda',
    workInquiries: 'Richieste di lavoro',
    name: 'Nome',
    company: 'Azienda',
    phone: 'Telefono',
    message: 'Messaggio',
    budget: 'Budget',
    moreThan: 'Più di',
    currency: '€',
    sending: 'Invio in corso...',
    sent: 'Messaggio inviato. Grazie.',
    networkError: 'Errore di rete. Per favore riprova più tardi.',
    submitFailed: 'Invio fallito. Per favore riprova.',
    nameRequired: 'Per favore inserisci il tuo nome.',
    emailRequired: 'Per favore inserisci l\'indirizzo email.',
    emailInvalid: "Indirizzo email non valido.",
    verifySpam: 'Per favore verifica che non sei un bot di spam.',
    emailDisposable: "Questo indirizzo email è un'email temporanea. Per favore usa un indirizzo reale.",
  },
}

function TextInput({
                       label,
                       ...props
                   }: React.ComponentPropsWithoutRef<'input'> & { label: string }) {
    let id = useId()

    return (
        <div className="group relative z-0 transition-all focus-within:z-10">
            <input
                type="text"
                id={id}
                {...props}
                placeholder=" "
                className="peer block w-full border border-neutral-300 bg-transparent px-6 pt-12 pb-4 text-base/6 text-neutral-950 ring-4 ring-transparent transition group-first:rounded-t-2xl group-last:rounded-b-2xl focus:border-neutral-950 focus:ring-neutral-950/5 focus:outline-hidden"
            />
            <label
                htmlFor={id}
                className="pointer-events-none absolute top-1/2 left-6 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-not-placeholder-shown:-translate-y-4 peer-not-placeholder-shown:scale-75 peer-not-placeholder-shown:font-semibold peer-not-placeholder-shown:text-neutral-950 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950"
            >
                {label}
            </label>
        </div>
    )
}

function RadioInput({
                        label,
                        ...props
                    }: React.ComponentPropsWithoutRef<'input'> & { label: string }) {
    return (
        <label className="flex gap-x-3">
            <input
                type="radio"
                {...props}
                className="h-6 w-6 flex-none appearance-none rounded-full border border-neutral-950/20 outline-hidden checked:border-[0.5rem] checked:border-neutral-950 focus-visible:ring-1 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
            />
            <span className="text-base/6 text-neutral-950">{label}</span>
        </label>
    )
}

export function Intro() {
    const { language } = useLanguage();
    return (
        <PageIntro eyebrow={texts[language as keyof typeof texts]?.contactUs || texts['en'].contactUs} title={texts[language as keyof typeof texts]?.title || texts['en'].title}>
            <p>{texts[language as keyof typeof texts]?.text || texts['en'].text}</p>
        </PageIntro>
    )
}

export function ContactDetail () {
    const { language } = useLanguage();
    return (
        <FadeIn>
            <h2 className="font-display text-base font-semibold text-neutral-950">
                {texts[language as keyof typeof texts]?.enterpriseDetails || texts['en'].enterpriseDetails}
            </h2>

            <Offices className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2" />

            <Border className="mt-16 pt-16">
                <h2 className="font-display text-base font-semibold text-neutral-950">
                    {texts[language as keyof typeof texts]?.emailUs || texts['en'].emailUs}
                </h2>
                <dl className="mt-6 grid grid-cols-1 gap-8 text-sm sm:grid-cols-2">
                    {[
                        ['Info', 'info@eptacode.com'],
                    ].map(([label, email]) => (
                        <div key={email}>
                            <dt className="font-semibold text-neutral-950">{label}</dt>
                            <dd>
                                <Link
                                    href={`mailto:${email}`}
                                    className="text-neutral-600 hover:text-neutral-950"
                                >
                                    {email}
                                </Link>
                            </dd>
                        </div>
                    ))}
                </dl>
            </Border>
        </FadeIn>
    )
}

export function ContactForm() {
  const { language } = useLanguage();
  const [status, setStatus] = useState<
    | { state: 'idle' }
    | { state: 'sending' }
    | { state: 'success' }
    | { state: 'error'; message: string }
  >({ state: 'idle' });
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
  const tsRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState('')

  const renderTurnstile = () => {
    if (!siteKey) return
    if (!tsRef.current) return
    if (!window.turnstile) return
    if (widgetIdRef.current) return

    widgetIdRef.current = window.turnstile.render(tsRef.current, {
      sitekey: siteKey,
      theme: 'auto',
      callback: (t: string) => setTurnstileToken(t),
      'error-callback': () => setTurnstileToken(''),
      'expired-callback': () => setTurnstileToken(''),
    })
  }
  useEffect(() => {
    renderTurnstile()
  }, [siteKey])

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus({ state: 'sending' })

    const form = e.currentTarget
    const fd = new FormData(form)

    // Se vuoi: validazioni minime lato client
    const email = String(fd.get('email') || '').trim()
    const name = String(fd.get('name') || '').trim()
    switch (true) {
      case !name:
        setStatus({ state: 'error', message: texts[language as keyof typeof texts]?.nameRequired ||
            texts['en'].nameRequired })
        return
      case !email:
        setStatus({ state: 'error', message: texts[language as keyof typeof texts]?.emailRequired ||
            texts['en'].emailRequired })
        return
      case !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email):
        setStatus({ state: 'error', message: texts[language as keyof typeof texts]?.emailInvalid ||
            texts['en'].emailInvalid })
        return
    }

    //if (!turnstileToken) {
    //  setStatus({ state: 'error', message: texts[language as keyof typeof texts]?.verifySpam ||texts['en'].verifySpam })
    //  return
    //}

    setStatus({ state: 'sending' });

    try {
      const v = await fetch('/api/validate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const vData = await v.json().catch(() => ({} as any));

      if (v.ok && vData?.disposable === true) {
        setStatus({
          state: 'error',
          message:
            texts[language as keyof typeof texts]?.emailDisposable ||
            texts['en'].emailDisposable,
        });
        return;
      }

      // fail-open: se v non è ok, ignoro e proseguo
      // fail-closed: se preferisci, qui fai return con errore
    } catch {
      // fail-open: proseguo comunque
    }

    const payload = {
      name,
      email,
      company: String(fd.get('company') || '').trim(),
      phone: String(fd.get('phone') || '').trim(),
      message: String(fd.get('message') || '').trim(),
      budget: String(fd.get('budget') || '').trim(),
      language,
      turnstileToken,
    }

  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    const data = await res.json().catch(() => ({}))

    if (!res.ok) {
      setStatus({ state: 'error', message: data?.error || texts[language as keyof typeof texts]?.submitFailed ||
          texts['en'].submitFailed })
      if (window.turnstile && widgetIdRef.current)
        window.turnstile.reset(widgetIdRef.current)
      setTurnstileToken('')
      return
    }

    if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current)
    setTurnstileToken('')

    setStatus({ state: 'success' })
    form.reset()
    } catch {
    if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current)
    setTurnstileToken('')
    setStatus({ state: 'error', message: texts[language as keyof typeof texts]?.networkError || texts['en'].networkError })
    }
  }

  return (
    <FadeIn className="lg:order-last">
      <form onSubmit={onSubmit} noValidate>
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          strategy="afterInteractive"
          onLoad={() => {
            // quando lo script è pronto, prova a renderizzare
            renderTurnstile()
          }}
        />
        <h2 className="font-display text-base font-semibold text-neutral-950">
          {texts[language as keyof typeof texts]?.workInquiries ||
            texts['en'].workInquiries}
        </h2>
        <div className="isolate mt-6 -space-y-px rounded-2xl bg-white/50">
          <TextInput
            label={
              texts[language as keyof typeof texts]?.name || texts['en'].name
            }
            name="name"
            autoComplete="name"
            required={true}
          />
          <TextInput
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required={true}
          />
          <TextInput
            label={
              texts[language as keyof typeof texts]?.company ||
              texts['en'].company
            }
            name="company"
            autoComplete="organization"
          />
          <TextInput
            label={
              texts[language as keyof typeof texts]?.phone || texts['en'].phone
            }
            type="tel"
            name="phone"
            autoComplete="tel"
          />
          <TextInput
            label={
              texts[language as keyof typeof texts]?.message ||
              texts['en'].message
            }
            name="message"
            required={true}
          />
          <div className="border border-neutral-300 px-6 py-8 first:rounded-t-2xl last:rounded-b-2xl">
            <fieldset>
              <legend className="text-base/6 text-neutral-500">
                {texts[language as keyof typeof texts]?.budget ||
                  texts['en'].budget}
              </legend>
              <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2">
                <RadioInput label="€1K – €2K" name="budget" value="1-2K" />
                <RadioInput label="€2K – €5K" name="budget" value="2-5K" />
                <RadioInput label="€5K – €10K" name="budget" value="5-10K" />
                <RadioInput
                  label={`${texts[language as keyof typeof texts]?.moreThan || texts['en'].moreThan} €10K`}
                  name="budget"
                  value="more-10K"
                />
              </div>
            </fieldset>
          </div>
        </div>
        <div ref={tsRef} />
        <Button
          type="submit"
          className="mt-10"
          //disabled={status.state === 'sending'}
          disabled={status.state === 'sending' || !turnstileToken}
        >
          {status.state === 'sending'
            ? texts[language as keyof typeof texts]?.sending ||
              texts['en'].sending
            : texts[language as keyof typeof texts]?.title || texts['en'].title}
        </Button>

        {status.state === 'success' && (
          <p className="mt-4 text-sm text-green-700">
            {texts[language as keyof typeof texts]?.sent || texts['en'].sent}
          </p>
        )}
        {status.state === 'error' && (
          <p className="mt-4 text-sm text-red-700">{status.message}</p>
        )}
      </form>
    </FadeIn>
  )
}
